import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Plus, Search, Sparkles, X } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Iter 124 — Selector de proveedor para el formulario de Gasto.
 *
 * Comportamiento:
 *  - Autocompleta contra GET /suppliers?q= (debounce 250ms).
 *  - Si el texto coincide EXACTAMENTE con un proveedor existente, muestra
 *    un badge "Existente" (para que quede claro que se va a reutilizar).
 *  - Si no coincide con ninguno, aparece la opción "Crear nuevo proveedor"
 *    que abre un modal con NIF/CIF, teléfono y email opcionales.
 *  - Al crear/seleccionar propaga `{ id, name, tax_id }` vía onChange.
 *  - El backend deduplica de todos modos por name/tax_id (`resolve_or_create_supplier`),
 *    así que este componente es un ayudante UX puro, no una regla de negocio.
 *
 * Props:
 *  - value: { id, name, tax_id } — proveedor actual (o solo `{ name }` para modo edición legacy).
 *  - onChange(next): callback con el nuevo proveedor seleccionado o creado.
 *  - autoFocus, disabled: passthrough.
 *  - testid: prefijo para data-testid (default "expense-supplier").
 */
export default function SupplierPicker({
  value,
  onChange,
  autoFocus = false,
  disabled = false,
  testid = "expense-supplier",
}) {
  const [input, setInput] = useState(value?.name || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(value || null); // proveedor confirmado
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  // Sync externo (edit inicial).
  useEffect(() => {
    if (value?.name && value.name !== input) {
      setInput(value.name);
      setSelected(value);
    }
  }, [value?.id, value?.name]);

  // Debounced query
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const q = input.trim();
      try {
        setLoading(true);
        const r = await api.get(`/suppliers?q=${encodeURIComponent(q)}`);
        setSuggestions(Array.isArray(r.data) ? r.data.slice(0, 8) : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [input, open]);

  // Cerrar al hacer click fuera.
  useEffect(() => {
    const onDown = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const exactMatch = suggestions.find(
    (s) => (s.name || "").trim().toLowerCase() === input.trim().toLowerCase()
  );

  const pick = (s) => {
    setSelected(s);
    setInput(s.name);
    setOpen(false);
    onChange({ id: s.id, name: s.name, tax_id: s.tax_id || "" });
  };

  const handleInputChange = (e) => {
    const v = e.target.value;
    setInput(v);
    setOpen(true);
    // Si el usuario edita, invalidamos la selección: quedará como texto libre
    // hasta que elija de la lista o pulse "Crear nuevo".
    if (selected && v.trim().toLowerCase() !== (selected.name || "").trim().toLowerCase()) {
      setSelected(null);
    }
    // Propagamos el texto libre — el backend hará resolve_or_create al guardar.
    onChange({ id: "", name: v, tax_id: "" });
  };

  const handleCreated = (sup) => {
    setShowCreate(false);
    setSuggestions((prev) => [sup, ...prev.filter((s) => s.id !== sup.id)]);
    pick(sup);
    toast.success(`Proveedor "${sup.name}" creado.`);
  };

  return (
    <div className="relative" ref={wrapperRef} data-testid={`${testid}-picker`}>
      <div className="relative">
        <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#525252]" />
        <input
          type="text"
          value={input}
          onChange={handleInputChange}
          onFocus={() => setOpen(true)}
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder="Buscar proveedor por nombre o NIF..."
          data-testid={testid}
          autoComplete="off"
          className="w-full ds-card focus:border-[#6E8B3D] outline-none pl-7 pr-24 py-2 text-sm rounded-none"
        />
        {selected?.id ? (
          <span
            data-testid={`${testid}-badge-existing`}
            className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#6E8B3D] text-white font-mono text-[9px] uppercase tracking-widest"
          >
            <Check size={9} /> existente
          </span>
        ) : input.trim() ? (
          <span
            data-testid={`${testid}-badge-new`}
            className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#1F4E79] text-white font-mono text-[9px] uppercase tracking-widest"
          >
            <Sparkles size={9} /> nuevo
          </span>
        ) : null}
      </div>

      {open && !disabled && (
        <div className="absolute z-30 left-0 right-0 top-full mt-1 border border-[#111110] bg-white shadow-hard max-h-72 overflow-y-auto" data-testid={`${testid}-dropdown`}>
          {loading && (
            <div className="px-3 py-2 text-xs text-[#525252] flex items-center gap-2">
              <Loader2 size={12} className="animate-spin" /> Buscando...
            </div>
          )}
          {!loading && suggestions.length === 0 && (
            <div className="px-3 py-2 text-xs text-[#525252] italic">
              Sin coincidencias. Escribe y crea uno nuevo abajo.
            </div>
          )}
          {suggestions.map((s) => (
            <button
              type="button"
              key={s.id}
              onClick={() => pick(s)}
              data-testid={`${testid}-option-${s.id}`}
              className="w-full text-left px-3 py-1.5 border-b border-[#E5E5E0] hover:bg-[#F0F7EC] text-sm flex items-center justify-between gap-2"
            >
              <span className="truncate">{s.name}</span>
              {s.tax_id ? (
                <span className="font-mono text-[10px] text-[#525252] shrink-0">{s.tax_id}</span>
              ) : null}
            </button>
          ))}
          {input.trim() && !exactMatch && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              data-testid={`${testid}-create-btn`}
              className="w-full text-left px-3 py-2 bg-[#F7F7F4] hover:bg-[#EAF2F8] text-sm font-semibold text-[#1F4E79] border-t-2 border-[#111110] inline-flex items-center gap-2"
            >
              <Plus size={13} /> Crear proveedor &quot;{input.trim()}&quot;...
            </button>
          )}
        </div>
      )}

      {showCreate && (
        <CreateSupplierModal
          defaultName={input.trim()}
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
}

function CreateSupplierModal({ defaultName, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: defaultName || "", tax_id: "", phone: "", email: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const name = form.name.trim();
    if (!name) {
      toast.error("El nombre / razón social es obligatorio.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/suppliers", {
        name,
        tax_id: form.tax_id.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
      });
      onCreated(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="supplier-create-modal"
    >
      <div
        className="bg-white border border-[#111110] shadow-hard w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E0]">
          <div className="font-display font-bold text-sm inline-flex items-center gap-2">
            <Plus size={14} /> Crear proveedor nuevo
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="supplier-create-close"
            className="p-1 hover:bg-[#F7F7F4]"
          >
            <X size={14} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Nombre / Razón social *">
            <input
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              data-testid="supplier-create-name"
              autoFocus
              className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm"
            />
          </Field>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="NIF / CIF (opcional)">
              <input
                value={form.tax_id}
                onChange={(e) => set("tax_id", e.target.value)}
                data-testid="supplier-create-taxid"
                className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm font-mono"
              />
            </Field>
            <Field label="Teléfono (opcional)">
              <input
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                data-testid="supplier-create-phone"
                className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm"
              />
            </Field>
          </div>
          <Field label="Email (opcional)">
            <input
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              data-testid="supplier-create-email"
              className="w-full ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm"
            />
          </Field>
          <p className="text-[11px] text-[#525252]">
            Si ya existe un proveedor con el mismo nombre o NIF, se reutilizará
            automáticamente y no se creará un duplicado.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#E5E5E0]">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm border border-[#E5E5E0] hover:bg-[#F7F7F4]"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !form.name.trim()}
            data-testid="supplier-create-save"
            className="inline-flex items-center gap-1 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-1.5 text-sm font-semibold disabled:opacity-50"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
            {saving ? "Guardando..." : "Crear proveedor"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252] mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}
